import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import L from 'leaflet'
import 'leaflet.markercluster'
import { createRoot } from 'react-dom/client'
import { deferUnmountRoot } from '@/shared/defer-unmount-root'
import {
  mapArrowTooltipLeafletOptions,
  scheduleLeafletOverlayLayoutUpdate,
} from '@/shared/map-arrow-tooltip-leaflet'
import { useEffect, useId, useRef, useState, useMemo } from 'react'
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Tooltip,
  Polyline,
  Polygon,
  Circle,
  ScaleControl,
  LayersControl,
  ZoomControl,
  useMap,
  useMapEvents,
} from 'react-leaflet'
import { twMerge } from 'tailwind-merge'
import type {
  MapProps,
  MapMarker as MapMarkerType,
  MapPolyline as MapPolylineType,
  MapPolygon as MapPolygonType,
  MapCircle as MapCircleType,
  MapTileLayerPreset,
  MapTileLayerConfig,
} from './types'
import type { LatLngExpression } from 'leaflet'

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

// React 19 StrictMode double-invokes cleanup: the second call to _removeIcon finds
// this._shadow = null/undefined (already cleared by the first call), causing DomEvent.off to crash.
// Patching L.DomEvent.off is unreliable because Vite inlines the internal call as a direct reference.
// Patching L.Marker.prototype._removeIcon is safe — prototype lookups are never inlined.
type MarkerProto = L.Marker & {
  _removeIcon(): void
  _icon: HTMLElement | null
  _shadow: HTMLElement | null
}
const _proto = L.Marker.prototype as unknown as MarkerProto
const _nativeRemoveIcon = _proto._removeIcon
_proto._removeIcon = function (this: MarkerProto) {
  try {
    _nativeRemoveIcon.call(this)
  } catch {
    // Second cleanup: _icon/_shadow already null — silence the error and ensure clean state.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ; (this as any)._icon = null
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ; (this as any)._shadow = null
  }
}

const TILE_LAYERS: Record<Exclude<MapTileLayerPreset, 'custom'>, MapTileLayerConfig> = {
  openstreetmap: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
    label: 'Padrão',
  },
  'openstreetmap-hot': {
    url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> Humanitarian',
    maxZoom: 19,
    label: 'Humanitário',
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
    maxZoom: 19,
    label: 'Satélite',
  },
  terrain: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
    maxZoom: 17,
    label: 'Terreno',
  },
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
    maxZoom: 19,
    label: 'Escuro',
  },
  light: {
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
    maxZoom: 19,
    label: 'Claro',
  },
}

function resolveTileLayer(
  preset: MapTileLayerPreset = 'openstreetmap',
  custom?: MapTileLayerConfig,
): MapTileLayerConfig {
  if (preset === 'custom' && custom) return custom
  return TILE_LAYERS[preset as Exclude<MapTileLayerPreset, 'custom'>]
}

function MapEventHandler({
  onClick,
  onMoveEnd,
  onZoomEnd,
}: Pick<MapProps, 'onClick' | 'onMoveEnd' | 'onZoomEnd'>) {
  useMapEvents({
    click(e) {
      onClick?.({ latlng: e.latlng, originalEvent: e.originalEvent })
    },
    moveend(e) {
      const map = e.target as L.Map
      onMoveEnd?.(map.getCenter(), map.getZoom())
    },
    zoomend(e) {
      const map = e.target as L.Map
      onZoomEnd?.(map.getZoom())
    },
  })
  return null
}

function MapReadyHandler({ onMapReady }: Pick<MapProps, 'onMapReady'>) {
  const map = useMap()
  const called = useRef(false)
  useEffect(() => {
    if (!called.current) {
      called.current = true
      onMapReady?.(map)
    }
  }, [map, onMapReady])
  return null
}

function BoundsFitter({
  bounds,
  padding = [20, 20],
  maxZoom,
}: {
  bounds: import('leaflet').LatLngBoundsExpression
  padding?: [number, number]
  maxZoom?: number
}) {
  const map = useMap()
  useEffect(() => {
    const fit = () => {
      map.invalidateSize({ animate: false })
      map.fitBounds(bounds, {
        padding,
        ...(maxZoom != null ? { maxZoom } : {}),
      })
    }
    const frame = requestAnimationFrame(fit)
    return () => cancelAnimationFrame(frame)
  }, [map, bounds, padding, maxZoom])
  return null
}

function FitBoundsButton({
  markers,
  position = 'topright',
}: {
  markers?: MapMarkerType[]
  position?: MapProps['fitBoundsButtonPosition']
}) {
  const map = useMap()

  const fit = () => {
    if (!markers?.length) return
    const latlngs = markers.map((m) => m.position as L.LatLngExpression)
    const bounds = L.latLngBounds(latlngs as L.LatLngExpression[])
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] })
  }

  const posClass: Record<NonNullable<MapProps['fitBoundsButtonPosition']>, string> = {
    topleft: 'top-2 left-2',
    topright: 'top-2 right-2',
    bottomleft: 'bottom-8 left-2',
    bottomright: 'bottom-8 right-2',
  }

  return (
    <div className={`leaflet-control absolute z-1000 ${posClass[position ?? 'topright']}`}>
      <button
        onClick={fit}
        title="Enquadrar marcadores"
        className="bg-white border border-[#ccc] rounded shadow-sm w-8 h-8 flex items-center justify-center text-[#444] hover:bg-gray-50 cursor-pointer"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
        </svg>
      </button>
    </div>
  )
}

// Tamanho fixo do triângulo direcional — manter constante evita rebuild de DivIcon a cada zoom
const ARROW_SIZE = 14
// Densidade visual: maior = menos setas exibidas em cada nível de zoom (espelha portal Angular)
const ARROW_DENSITY = 8

function haversineMeters(a: L.LatLng, b: L.LatLng): number {
  const R = 6371000
  const φ1 = (a.lat * Math.PI) / 180
  const φ2 = (b.lat * Math.PI) / 180
  const Δφ = ((b.lat - a.lat) * Math.PI) / 180
  const Δλ = ((b.lng - a.lng) * Math.PI) / 180
  const x =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x))
}

function getMapScaleMeters(map: L.Map): number {
  const zoom = map.getZoom()
  const lat = map.getCenter().lat
  return (591657550.5 / Math.pow(2, zoom - 1)) * Math.cos((lat * Math.PI) / 180)
}

function bearing(from: L.LatLng, to: L.LatLng): number {
  const φ1 = (from.lat * Math.PI) / 180
  const φ2 = (to.lat * Math.PI) / 180
  const Δλ = ((to.lng - from.lng) * Math.PI) / 180
  const y = Math.sin(Δλ) * Math.cos(φ2)
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360
}

function createArrowDivIcon(color: string, bearingDeg: number, size: number): L.DivIcon {
  // O SVG aponta para cima (Norte) por padrão; rotacionamos pelo bearing
  const half = size / 2
  return L.divIcon({
    className: '',
    iconSize: L.point(size, size),
    iconAnchor: L.point(half, half),
    tooltipAnchor: L.point(0, -half),
    html: `<svg xmlns="http://www.w3.org/2000/svg"
                width="${size}" height="${size}"
                viewBox="0 0 10 10"
                style="display:block;transform:rotate(${bearingDeg - 90}deg)">
             <polygon points="10,5 0,0 0,10"
                      fill="${color}"
                      stroke="#ffffff"
                      stroke-width="0.75"
                      stroke-linejoin="round" />
           </svg>`,
  })
}

function ArrowsLayer({
  polylines,
  spacing = 25,
}: {
  polylines: MapPolylineType[]
  spacing?: number
}) {
  const map = useMap()

  useEffect(() => {
    type ArrowEntry = { marker: L.Marker; visible: boolean }
    const arrowEntries: ArrowEntry[] = []
    const rootCleanups: Array<() => void> = []

    const spacingMeters = Math.max(1, spacing)

    for (const pl of polylines) {
      const pts = pl.positions
      const color = (pl.pathOptions?.color as string | undefined) ?? '#005A9E'
      const segMeters = pl.segmentMeters
      let accumulated = 0

      for (let i = 0; i < pts.length - 2; i++) {
        const from = L.latLng(pts[i] as L.LatLngExpression)
        const to = L.latLng(pts[i + 1] as L.LatLngExpression)
        const delta = segMeters?.[i]
        accumulated += Number.isFinite(delta) && delta! >= 0 ? delta! : haversineMeters(from, to)

        if (accumulated < spacingMeters) continue
        accumulated = 0

        const bearingDeg = bearing(from, to)
        const icon = createArrowDivIcon(color, bearingDeg, ARROW_SIZE)

        const arrow = L.marker(to, {
          icon,
          interactive: pl.arrowPopup != null,
          zIndexOffset: -100,
        })

        arrowEntries.push({ marker: arrow, visible: false })

        if (pl.arrowPopup != null) {
          const segmentIndex = i + 1
          let tooltipRoot: ReturnType<typeof createRoot> | null = null
          let tooltipMounted = false
          const tooltipEl = document.createElement('div')

          arrow.bindTooltip(tooltipEl, mapArrowTooltipLeafletOptions())

          // O Leaflet liga, por padrão, `mouseout: closeTooltip` (e mouseover/click)
          // no marcador, fechando o tooltip antes do cursor alcançar o conteúdo.
          // Removemos esses handlers e controlamos abrir/fechar pelo hover-bridge
          // abaixo. Mantemos os handlers internos `remove`/`move`.
          const leafletArrow = arrow as unknown as Record<string, L.LeafletEventHandlerFn>
          arrow.off('mouseover', leafletArrow._openTooltip)
          arrow.off('mouseout', leafletArrow.closeTooltip)
          arrow.off('click', leafletArrow._openTooltip)

          const ensureTooltipContent = () => {
            if (tooltipRoot) return
            tooltipRoot = createRoot(tooltipEl)
            tooltipRoot.render(pl.arrowPopup!(segmentIndex) as React.ReactElement)
            const root = tooltipRoot
            rootCleanups.push(() => deferUnmountRoot(root))
          }

          let closeTimer: ReturnType<typeof setTimeout> | null = null
          let overArrow = false
          let overTooltip = false

          const cancelClose = () => {
            if (closeTimer) {
              clearTimeout(closeTimer)
              closeTimer = null
            }
          }

          // Fecha só quando o cursor não está nem sobre a seta nem sobre o
          // tooltip, com folga pra atravessar o vão entre os dois.
          const scheduleClose = () => {
            cancelClose()
            closeTimer = setTimeout(() => {
              if (!overArrow && !overTooltip) arrow.closeTooltip()
            }, 300)
          }

          arrow.on('mouseover', () => {
            overArrow = true
            const firstOpen = !tooltipMounted
            ensureTooltipContent()
            cancelClose()
            arrow.openTooltip()
            if (firstOpen) {
              tooltipMounted = true
              scheduleLeafletOverlayLayoutUpdate(arrow.getTooltip())
            }
          })

          arrow.on('mouseout', () => {
            overArrow = false
            scheduleClose()
          })

          arrow.on('tooltipopen', () => {
            const tooltipDom = arrow.getTooltip()?.getElement()
            if (!tooltipDom) return

            tooltipDom.addEventListener('mouseenter', () => {
              overTooltip = true
              cancelClose()
            })
            tooltipDom.addEventListener('mouseleave', () => {
              overTooltip = false
              scheduleClose()
            })
          })
        }
      }
    }

    const updateVisibility = () => {
      // Mesma fórmula do portal Angular: distância acumulada vs. escala do mapa / densidade
      const threshold = getMapScaleMeters(map) / 100 / ARROW_DENSITY
      let acc = 0
      for (const entry of arrowEntries) {
        if (acc >= threshold) {
          if (!entry.visible) {
            entry.marker.addTo(map)
            entry.visible = true
          }
          acc = 0
        } else if (entry.visible) {
          entry.marker.removeFrom(map)
          entry.visible = false
        }
        acc += spacingMeters
      }
    }

    updateVisibility()
    map.on('zoomend', updateVisibility)

    return () => {
      map.off('zoomend', updateVisibility)
      for (const entry of arrowEntries) {
        if (entry.visible) entry.marker.removeFrom(map)
      }
      rootCleanups.forEach((c) => c())
    }
  }, [map, polylines, spacing])

  return null
}

function ClusterControlPanel({ onChange }: { onChange?: (radius: number) => void }) {
  const [radius, setRadius] = useState(5)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ref.current) return
    L.DomEvent.disableClickPropagation(ref.current)
    L.DomEvent.disableScrollPropagation(ref.current)
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value)
    setRadius(val)
    onChange?.(val)
  }

  return (
    <div
      ref={ref}
      className="absolute right-4 bottom-12 z-[1000] flex items-center gap-3 rounded-lg border border-white/70 bg-white/85 px-4 py-2 shadow-lg backdrop-blur"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
      >
        <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1116 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
      <input
        type="range"
        min={0}
        max={40}
        value={radius}
        onChange={handleChange}
        className="w-28 accent-primary"
        title={`Agrupamento: ${radius}`}
      />
    </div>
  )
}

function MarkerLayer({ markers }: { markers: MapMarkerType[] }) {
  return (
    <>
      {markers.map((m) => (
        <Marker
          key={m.id}
          position={m.position}
          {...(m.icon != null ? { icon: m.icon } : {})}
          draggable={m.draggable}
          eventHandlers={{
            click: m.onClick,
            mouseover(e) {
              if (m.popupOnHover) {
                ; (e.target as L.Marker).openPopup()
              }
            },
            dragend(e) {
              const latlng = (e.target as L.Marker).getLatLng()
              m.onDragEnd?.(latlng)
            },
          }}
        >
          {m.popup != null && (
            <Popup className={m.popupClassName} autoPan={!m.popupOnHover}>
              {m.popup}
            </Popup>
          )}
          {m.tooltip != null && (
            <Tooltip permanent={m.tooltipPermanent} className={m.tooltipClassName}>
              {m.tooltip}
            </Tooltip>
          )}
        </Marker>
      ))}
    </>
  )
}

function ClusterMarkerLayer({
  markers,
  maxClusterRadius = 80,
}: {
  markers: MapMarkerType[]
  maxClusterRadius?: number
}) {
  const map = useMap()

  useEffect(() => {
    const roots: ReturnType<typeof createRoot>[] = []
    type ClusterMarker = L.Marker & {
      options: L.MarkerOptions & {
        clusterColor?: string
        clusterPriority?: number
      }
    }

    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius,
      spiderfyOnMaxZoom: true,
      iconCreateFunction(cluster) {
        const count = cluster.getChildCount()
        const clusterColor =
          (cluster.getAllChildMarkers() as ClusterMarker[]).reduce(
            (selected, marker) => {
              const color = marker.options.clusterColor
              const priority = marker.options.clusterPriority ?? -1
              if (!color || priority < selected.priority) return selected
              return { color, priority }
            },
            { color: '#8EC63F', priority: -1 },
          ).color

        return L.divIcon({
          html: `<div class="rsp-cluster-icon" style="--rsp-cluster-color:${clusterColor}">${count}</div>`,
          className: '',
          iconSize: L.point(40, 40),
          iconAnchor: L.point(20, 20),
        })
      },
    })

    for (const m of markers) {
      const marker = L.marker(m.position as L.LatLngExpression, {
        ...(m.icon != null ? { icon: m.icon } : {}),
        draggable: m.draggable,
        // clusterColor: m.clusterColor,
        // clusterPriority: m.clusterPriority,
      })

      if (m.tooltip != null) {
        const el = document.createElement('div')
        const root = createRoot(el)
        root.render(m.tooltip as React.ReactElement)
        roots.push(root)
        marker.bindTooltip(el, {
          className: m.tooltipClassName ?? '',
          direction: 'top',
          offset: [0, -28],
        })
      }

      if (m.popup != null) {
        const el = document.createElement('div')
        const root = createRoot(el)
        root.render(m.popup as React.ReactElement)
        roots.push(root)
        marker.bindPopup(el, {
          className: m.popupClassName,
          autoPan: !m.popupOnHover,
        })
      }

      if (m.onClick) marker.on('click', m.onClick)
      if (m.popupOnHover) {
        marker.on('mouseover', () => {
          if (!marker.isPopupOpen()) {
            marker.openPopup()
          }
        })
      }
      if (m.draggable && m.onDragEnd) {
        marker.on('dragend', (e) => {
          m.onDragEnd?.((e.target as L.Marker).getLatLng())
        })
      }

      group.addLayer(marker)
    }

    map.addLayer(group)

    return () => {
      map.removeLayer(group)
      roots.forEach((r) => deferUnmountRoot(r))
    }
  }, [map, markers, maxClusterRadius])

  return null
}

function PolylineLayer({ polylines }: { polylines: MapPolylineType[] }) {
  return (
    <>
      {polylines.map((p) => (
        <Polyline key={p.id} positions={p.positions} pathOptions={p.pathOptions}>
          {p.popup != null && <Popup>{p.popup}</Popup>}
          {p.tooltip != null && <Tooltip>{p.tooltip}</Tooltip>}
        </Polyline>
      ))}
    </>
  )
}

function PolygonLayer({ polygons }: { polygons: MapPolygonType[] }) {
  return (
    <>
      {polygons.map((p) => (
        <Polygon
          key={p.id}
          positions={p.positions as LatLngExpression[]}
          pathOptions={p.pathOptions}
        >
          {p.popup != null && <Popup>{p.popup}</Popup>}
          {p.tooltip != null && <Tooltip>{p.tooltip}</Tooltip>}
        </Polygon>
      ))}
    </>
  )
}

function CircleLayer({ circles }: { circles: MapCircleType[] }) {
  return (
    <>
      {circles.map((c) => (
        <Circle key={c.id} center={c.center} radius={c.radius} pathOptions={c.pathOptions}>
          {c.popup != null && <Popup>{c.popup}</Popup>}
          {c.tooltip != null && <Tooltip>{c.tooltip}</Tooltip>}
        </Circle>
      ))}
    </>
  )
}

export function Map({
  id,
  type,
  height = '400px',
  width = '100%',
  className,
  center = [-14.235, -51.9253],
  zoom = 5,
  minZoom,
  maxZoom,
  bounds,
  boundsPadding = [20, 20],
  fitBoundsMaxZoom,
  tileLayer = 'openstreetmap',
  customTileLayer,
  showLayerControl = false,
  additionalLayers = [],
  markers = [],
  polylines = [],
  polygons = [],
  circles = [],
  zoomControl = true,
  zoomControlPosition = 'topleft',
  scaleControl = false,
  scaleControlPosition = 'bottomleft',
  fitBoundsButton = false,
  fitBoundsButtonPosition = 'topright',
  scrollWheelZoom = true,
  dragging = true,
  doubleClickZoom = true,
  showArrows,
  arrowSpacing = 1,
  clusterMarkers,
  clusterControl,
  onClusterRadiusChange,
  onClick,
  onMoveEnd,
  onZoomEnd,
  onMapReady,
  children,
}: MapProps) {
  const generatedId = useId()
  const mapKey = id ?? generatedId
  const activeTile = resolveTileLayer(tileLayer, customTileLayer)
  const effectiveShowArrows = showArrows ?? type === 'trip'
  const effectiveClusterMarkers = clusterMarkers ?? type === 'consolidated'
  const effectiveClusterControl = clusterControl ?? type != null
  const zoomControlOffsetTop = zoomControlPosition === 'topleft' ? 40 : 0
  const clusteredMarkers = useMemo(() => effectiveClusterMarkers
    ? markers.filter((marker) => marker.cluster !== false)
    : [], [effectiveClusterMarkers, markers])
  const regularMarkers = useMemo(() => effectiveClusterMarkers
    ? markers.filter((marker) => marker.cluster === false)
    : markers, [effectiveClusterMarkers, markers])

  // slider default=5 → 5*4.75+10=33.75 ≈ 34px — inicializa sincronizado com o ClusterControlPanel
  const [clusterRadius, setClusterRadius] = useState(34)
  const handleClusterRadiusChange = (sliderValue: number) => {
    // slider 0-40 → maxClusterRadius 10-200px
    setClusterRadius(Math.round(sliderValue * 4.75 + 10))
    onClusterRadiusChange?.(sliderValue)
  }

  return (
    <div
      style={{ height, width }}
      className={twMerge('relative overflow-hidden rounded-xl', className)}
    >
      {zoomControlOffsetTop > 0 && (
        <style>{`[id="${mapKey}"] .leaflet-top.leaflet-left .leaflet-control-zoom { margin-top: ${10 + zoomControlOffsetTop}px; }`}</style>
      )}
      {scaleControl && (
        <style>{`[id="${mapKey}"] .leaflet-control-scale { margin-top: 10px; margin-left: 10px; }`}</style>
      )}
      <MapContainer
        key={mapKey}
        id={mapKey}
        center={center}
        zoom={zoom}
        minZoom={minZoom}
        maxZoom={maxZoom}
        zoomControl={false}
        scrollWheelZoom={scrollWheelZoom}
        dragging={dragging}
        doubleClickZoom={doubleClickZoom}
        preferCanvas
        style={{ height: '100%', width: '100%' }}
      >
        {showLayerControl && additionalLayers.length > 0 ? (
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name={activeTile.label ?? 'Padrão'}>
              <TileLayer
                url={activeTile.url}
                attribution={activeTile.attribution}
                maxZoom={activeTile.maxZoom}
              />
            </LayersControl.BaseLayer>
            {additionalLayers.map((layer, i) => (
              <LayersControl.BaseLayer key={i} name={layer.label ?? `Camada ${i + 1}`}>
                <TileLayer
                  url={layer.url}
                  attribution={layer.attribution}
                  maxZoom={layer.maxZoom}
                />
              </LayersControl.BaseLayer>
            ))}
          </LayersControl>
        ) : (
          <TileLayer
            url={activeTile.url}
            attribution={activeTile.attribution}
            maxZoom={activeTile.maxZoom}
          />
        )}

        {zoomControl && <ZoomControl position={zoomControlPosition} />}
        {scaleControl && <ScaleControl position={scaleControlPosition} imperial={false} />}
        {bounds && (
          <BoundsFitter bounds={bounds} padding={boundsPadding} maxZoom={fitBoundsMaxZoom} />
        )}
        {fitBoundsButton && markers.length > 0 && (
          <FitBoundsButton markers={markers} position={fitBoundsButtonPosition} />
        )}

        {clusteredMarkers.length > 0 && effectiveClusterMarkers && (
          <ClusterMarkerLayer markers={clusteredMarkers} maxClusterRadius={clusterRadius} />
        )}
        {regularMarkers.length > 0 && <MarkerLayer markers={regularMarkers} />}
        {polylines.length > 0 && <PolylineLayer polylines={polylines} />}
        {effectiveShowArrows && polylines.length > 0 && (
          <ArrowsLayer polylines={polylines} spacing={arrowSpacing} />
        )}
        {polygons.length > 0 && <PolygonLayer polygons={polygons} />}
        {circles.length > 0 && <CircleLayer circles={circles} />}
        <MapEventHandler onClick={onClick} onMoveEnd={onMoveEnd} onZoomEnd={onZoomEnd} />
        {onMapReady && <MapReadyHandler onMapReady={onMapReady} />}

        {children}
      </MapContainer>
      {effectiveClusterControl && (
        <ClusterControlPanel onChange={handleClusterRadiusChange} />
      )}
    </div>
  )
}
